import type { ActionParamMetadataOutDTO } from '@api/ringil-api.v2';
import type { RuleValue } from '../../ruleAst';

/**
 * "How much" for an action that takes either a fixed `amount` or a `percent` of something —
 * today `add_extra_cost`.
 *
 * The two parameters are mutually exclusive, and `of` only means anything next to `percent`. The
 * backend refuses neither-or-both only at run time (`aidispatcher.action.error.amount_or_percent`),
 * the validator does not catch it, so the builder offers one choice of three instead of three
 * independent fields an author could fill in any combination.
 *
 * Detected from the parameter shape, never from the action's key, so any future action declaring
 * the same trio gets the same switch.
 */
export type AmountMode = 'FIXED' | 'PERCENT_OF_ORIGINAL' | 'PERCENT_OF_TOTAL';

/** The parameters the switch owns; the builder renders them itself rather than as plain fields. */
export const AMOUNT_MODE_PARAMS = ['amount', 'percent', 'of'];

const ORIGINAL = 'ORIGINAL';
const TOTAL = 'TOTAL';

type ParamLike = Pick<ActionParamMetadataOutDTO, 'name' | 'paramType' | 'enumValues'>;

export const hasAmountMode = (params: ParamLike[] | undefined): boolean => {
  const byName = (name: string) => (params ?? []).find(param => param.name === name);
  const amount = byName('amount');
  const percent = byName('percent');
  const of = byName('of');
  return (
    String(amount?.paramType) === 'MONEY' &&
    String(percent?.paramType) === 'NUMBER' &&
    String(of?.paramType) === 'ENUM' &&
    (of?.enumValues ?? []).includes(ORIGINAL) &&
    (of?.enumValues ?? []).includes(TOTAL)
  );
};

const isSet = (value: RuleValue | undefined) => value !== null && value !== undefined;

/**
 * Which choice the stored parameters express. Before either value is entered the parameters say
 * nothing, so the caller's own remembered choice (`fallback`) stands — otherwise picking "% of
 * original" on an empty action would snap straight back to "Fixed".
 */
export const amountModeOf = (params: Record<string, RuleValue> | undefined, fallback: AmountMode = 'FIXED'): AmountMode => {
  if (isSet(params?.amount)) return 'FIXED';
  if (params?.of === TOTAL) return 'PERCENT_OF_TOTAL';
  if (isSet(params?.percent)) return 'PERCENT_OF_ORIGINAL';
  return fallback;
};

/**
 * The parameters after switching to `mode`: the parameter the other choice used is dropped rather
 * than nulled (a null prints as `percent: null` in the rule text), and `of` is written only for
 * "% of total" — "% of original" is the backend's default and reads as `percent: 8`, like the
 * hand-over's examples. A percentage survives a switch between the two percentage choices.
 */
export const withAmountMode = (params: Record<string, RuleValue> | undefined, mode: AmountMode): Record<string, RuleValue> => {
  const { amount, percent } = params ?? {};
  const rest = Object.fromEntries(Object.entries(params ?? {}).filter(([name]) => !AMOUNT_MODE_PARAMS.includes(name)));
  switch (mode) {
    case 'FIXED':
      return isSet(amount) ? { ...rest, amount } : rest;
    case 'PERCENT_OF_ORIGINAL':
      return isSet(percent) ? { ...rest, percent } : rest;
    case 'PERCENT_OF_TOTAL':
      return isSet(percent) ? { ...rest, percent, of: TOTAL } : { ...rest, of: TOTAL };
  }
};

interface Text {
  key: string;
  text: string;
}

export const AMOUNT_MODE_OPTIONS: { mode: AmountMode; label: Text }[] = [
  { mode: 'FIXED', label: { key: 'AiDispatcher.amountMode.fixed', text: 'Fixed' } },
  { mode: 'PERCENT_OF_ORIGINAL', label: { key: 'AiDispatcher.amountMode.percentOfOriginal', text: '% of original' } },
  { mode: 'PERCENT_OF_TOTAL', label: { key: 'AiDispatcher.amountMode.percentOfTotal', text: '% of total' } },
];

/** What the chosen basis is computed from — the backend's semantics, said once under the switch. */
export const amountModeHint = (mode: AmountMode): Text => {
  switch (mode) {
    case 'FIXED':
      return { key: 'AiDispatcher.amountModeHint.fixed', text: 'Exactly this amount, in its own currency.' };
    case 'PERCENT_OF_ORIGINAL':
      return {
        key: 'AiDispatcher.amountModeHint.percentOfOriginal',
        text: 'Of the agreed carrier price, in its currency. Negative for a discount; rounded to 2 decimals.',
      };
    case 'PERCENT_OF_TOTAL':
      return {
        key: 'AiDispatcher.amountModeHint.percentOfTotal',
        text: 'Of the agreed price plus the extra costs already added — only carrier-visible ones for a carrier-visible cost, internal ones too for an internal cost. Mixed currencies are totalled in the company currency. Negative for a discount; rounded to 2 decimals.',
      };
  }
};
